import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'

import { RootState } from 'slices'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store'

import { useTranslation } from 'react-i18next'

import { setActiveTargetIndex, setVisible, nextTarget, prevTarget } from 'slices/spotlight'

import useResizeObserver from '@react-hook/resize-observer'

import Tooltip, { TooltipRef } from 'devextreme-react/tooltip'
import Button from 'devextreme-react/button'

import './spotlight.scss'

const predefinedTargets: any = {
    menu: {
        selector: '#drawerMenu',
        tooltipPosition: { my: 'left center', at: 'right center' }
    },

    panels: {
        selector: '#ix-toolbars',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    navBar: {
        selector: '#ix-toolbars-nav',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    globalFilters: {
        selector: '#ix-toolbars-global-filters',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    tabs: {
        selector: '#ix-toolbars-tabs',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    actions: {
        selector: '#ix-toolbars-actions',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    themeSwitcher: {
        selector: '.themeSwitcher',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    languageSwitcher: {
        selector: '.ix-lang-toggle',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },
    settings: {
        selector: 'img[alt=avatar]',
        tooltipPosition: { my: 'right top', at: 'left bottom' }
    },

    page: {
        selector: '#content',
        tooltipPosition: { my: 'center top', at: 'center bottom' }
    },

    bottomPanel: {
        selector: '#ix-common-runtime',
        tooltipPosition: { my: 'center bottom', at: 'center top' }
    },
}

const TooltipComponent = () => {
    const dispatch = useAppDispatch()
    const { activeTargetIndex, targets } = useSelector((state: RootState) => state.spotlight)

    const { t, i18n } = useTranslation()

    const onSkip = useCallback(() => {
        dispatch(setActiveTargetIndex(0))
        dispatch(setVisible(false))
    }, [dispatch])

    const onNext = useCallback(() => {
        dispatch(nextTarget())
    }, [dispatch])

    const onPrev = useCallback(() => {
        dispatch(prevTarget())
    }, [dispatch])

    const currentTarget = useMemo(() => {
        return targets[activeTargetIndex]
    }, [targets, activeTargetIndex])

    return (
        <div className='ix-spotlight-tooltip'>
            <div className='ix-spotlight-tooltip--info'>
                <span>{ activeTargetIndex + 1 }/{ targets.length }</span>
                <span onClick={ onSkip } style={ { cursor: 'pointer' } }>{ t('welcomeslideshow:button.skip') }</span>
            </div>
            <div className='ix-spotlight-tooltip--text'>
                <h3>{ currentTarget?.title[i18n.language] }</h3>
                <p>{ currentTarget?.desc[i18n.language] }</p>
            </div>
            <div className='ix-spotlight-tooltip--buttons'>
                <Button onClick={ onPrev } disabled={ activeTargetIndex === 0 }>{ t('welcomeslideshow:button.prev') }</Button>
                <Button type='success' onClick={ onNext }>{ t(`welcomeslideshow:button.${activeTargetIndex === targets.length - 1 ? 'finish' : 'next'}`) }</Button>
            </div>
        </div>
    )
}

export const IxSpotlight = () => {
    const { targets, activeTargetIndex, visible } = useSelector((state: RootState) => state.spotlight)

    const [style, setStyle] = useState<any>({})
    const targetRef = useRef(null)
    const tooltipRef = useRef<TooltipRef>(null)

    const onResize = useCallback((e: any) => {
        const bRect = e.target.getBoundingClientRect()

        setStyle({
            position: 'fixed',
            top: `${ bRect.top - 8 }px`,
            left: `${ bRect.left - 8 }px`,
            width: `${ bRect.width + 16 }px`,
            height: `${ bRect.height + 16 }px`,

            boxShadow: '0 0 0 9998px #0f172a8c',
            borderRadius: '8px',

            zIndex: 1500
        })

        setTimeout(() => {
            tooltipRef.current?.instance().repaint()
        }, 0)
    }, [])

    useEffect(() => {
        const targetEl = document.querySelector(predefinedTargets[targets[activeTargetIndex]?.selector]?.selector)
        targetRef.current = targetEl
        targetEl && onResize({ target: targetEl })
    }, [targets, activeTargetIndex, onResize])
    useResizeObserver(targetRef, onResize)

    if (!visible) { return null }

    return (
        <div
            className='ix-spotlight-wrapper'
        >
            <div id='spotlight' style={ style }>
            <Tooltip
                target="#spotlight"
                visible={ visible }
                ref={ tooltipRef }
                position={ predefinedTargets[targets[activeTargetIndex]?.selector]?.tooltipPosition }
                contentComponent={ TooltipComponent }
                width='300px'
                wrapperAttr={ { id: 'ix-spotlight-tooltip' } }
            />
            </div>
        </div>
    )
}
